import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Check, ChevronRight } from "lucide-react";
import { isAxiosError } from "axios";
import api from "../lib/api";
import AuthFrame, { PasswordField } from "../components/design/AuthFrame";
import { authLink, authNext } from "../lib/authNavigation";
import { verificationRequestContext } from "../lib/verificationNavigation";

type Mode = "id" | "pw";

function extractErrorMessage(error: unknown, fallback: string) {
  const message = isAxiosError<{ message?: unknown }>(error) ? error.response?.data?.message : undefined;
  return typeof message === "string" && message.trim() && !/<[^>]+>/.test(message) ? message : fallback;
}

export default function FindAccount() {
  const location = useLocation();
  const navigate = useNavigate();
  const next = authNext(new URLSearchParams(location.search).get("next"));
  const { amountEntry } = verificationRequestContext(next);
  const loginPath = authLink("/login", next);
  const [mode, setMode] = useState<Mode>("id");
  const [stage, setStage] = useState<"identity" | "password" | "done">("identity");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const submitting = useRef(false);
  const errorRef = useRef<HTMLDivElement>(null);
  const firstField = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  useEffect(() => {
    if (stage === "identity") firstField.current?.focus();
    else panelRef.current?.focus({ preventScroll: true });
  }, [stage]);

  function switchMode(value: Mode) {
    if (busy) return;
    setMode(value); setStage("identity"); setError(""); setMessage(""); setPassword(""); setConfirm("");
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (submitting.current) return;
    setError("");
    if (!name.trim() || !phone.trim() || (mode === "pw" && !email.trim())) { setError("가입할 때 입력한 정보를 모두 입력해 주세요."); return; }
    if (!/^\+?[\d\s-]{9,20}$/.test(phone.trim())) { setError("연락처를 확인해 주세요. 예: 01012345678"); return; }
    if (mode === "pw" && stage === "identity") { setStage("password"); return; }
    if (mode === "pw" && password.length < 8) { setError("새 비밀번호는 8자 이상으로 설정해 주세요."); return; }
    if (mode === "pw" && password !== confirm) { setError("새 비밀번호와 비밀번호 확인이 일치하지 않습니다."); return; }
    submitting.current = true;
    setBusy(true);
    try {
      // 주류페이의 기존 본인정보 기반 찾기/재설정 API 계약을 유지합니다.
      const response = mode === "id"
        ? await api.post("/auth/find-id", { name: name.trim(), phone: phone.trim() })
        : await api.post("/auth/reset-password", { email: email.trim(), name: name.trim(), phone: phone.trim(), newPassword: password });
      if (response.data?.ok === false) { setError(response.data.message || "가입한 정보와 일치하는지 확인해 주세요."); return; }
      setMessage(response.data?.message || (mode === "id" ? "등록된 아이디를 찾았습니다." : "비밀번호가 변경되었습니다. 새 비밀번호로 로그인해 주세요."));
      setStage("done"); setPassword(""); setConfirm("");
    } catch (requestError: unknown) {
      setError(extractErrorMessage(requestError, "요청을 처리하지 못했습니다. 입력한 정보를 확인하고 다시 시도해 주세요."));
    } finally { submitting.current = false; setBusy(false); }
  }

  return <AuthFrame title="계정 찾기" heading={stage === "done" ? mode === "id" ? "아이디 찾기 결과" : "비밀번호 변경 완료" : stage === "password" ? "새 비밀번호" : undefined} onBack={() => stage === "password" ? (setStage("identity"), setError("")) : navigate(loginPath)} busy={busy} steps={amountEntry ? ["송금 금액", "정보·서류", "최종 확인"] : undefined} step={1} progressLabel="결제 요청">
    <div className="ja-tabs" role="tablist" aria-label="계정 찾기 방법">{(["id", "pw"] as const).map(value => <button key={value} id={`account-tab-${value}`} type="button" role="tab" aria-selected={mode === value} aria-controls="account-panel" tabIndex={mode === value ? 0 : -1} disabled={busy} onKeyDown={event => { if (event.key === "ArrowLeft" || event.key === "ArrowRight") { event.preventDefault(); const other = value === "id" ? "pw" : "id"; switchMode(other); document.getElementById(`account-tab-${other}`)?.focus(); } }} onClick={() => switchMode(value)}>{value === "id" ? "아이디 찾기" : "비밀번호 재설정"}</button>)}</div>
    <div ref={panelRef} id="account-panel" className="ja-step-region" role="tabpanel" tabIndex={-1} aria-labelledby={`account-tab-${mode}`}>
      {mode === "pw" && stage !== "done" && <div className="ja-small-step" aria-label="비밀번호 재설정 순서"><span className={stage === "identity" ? "is-active" : ""}>01 가입 정보 입력</span><ChevronRight size={13} /><span className={stage === "password" ? "is-active" : ""}>02 새 비밀번호</span></div>}
      {error && <div ref={errorRef} tabIndex={-1} role="alert" className="ja-alert is-error">{error}</div>}
      {stage === "done" ? <div className="ja-complete">
        <div className="ja-complete-icon"><Check size={28} /></div>
        <div role="status" className="ja-result">{message}</div>
        <Link className="ja-primary" to={loginPath} state={mode === "pw" ? { email } : undefined}>로그인하기</Link>
        {mode === "id" && <button className="ja-secondary" type="button" onClick={() => switchMode("pw")}>비밀번호도 재설정하기</button>}
      </div> : <form onSubmit={submit} className="ja-form" aria-busy={busy}>
        {stage === "identity" ? <>
          {mode === "pw" && <div className="ja-field"><label htmlFor="find-email">이메일</label><input ref={firstField} id="find-email" type="email" required autoComplete="username" autoCapitalize="none" spellCheck={false} value={email} onChange={e => setEmail(e.target.value)} placeholder="가입한 이메일" disabled={busy} /></div>}
          <div className="ja-field"><label htmlFor="find-name">이름</label><input ref={mode === "id" ? firstField : undefined} id="find-name" required autoComplete="name" value={name} onChange={e => setName(e.target.value)} placeholder="가입한 이름" disabled={busy} /></div>
          <div className="ja-field"><label htmlFor="find-phone">연락처</label><input id="find-phone" required type="tel" autoComplete="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="가입한 연락처" disabled={busy} /></div>
        </> : <>
          <p className="ja-hint">{email}</p>
          <PasswordField id="find-password" label="새 비밀번호" required minLength={8} autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="8자 이상 입력" disabled={busy} />
          <PasswordField id="find-confirm" label="새 비밀번호 확인" required minLength={8} autoComplete="new-password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="새 비밀번호를 한 번 더 입력" aria-invalid={!!confirm && password !== confirm} disabled={busy} />
        </>}
        <button className="ja-primary" type="submit" disabled={busy}>{busy ? "처리 중…" : mode === "id" ? "아이디 찾기" : stage === "identity" ? "계속" : "비밀번호 변경"}</button>
      </form>}
    </div>
    {stage !== "done" && <p className="ja-switch"><Link className="ja-link" to={loginPath}>로그인으로 돌아가기</Link></p>}
  </AuthFrame>;
}
